import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../lib/auth'
import { api } from '../lib/api'
import { NotificationBell } from './NotificationBell'

export function HeaderNav() {
  const { user, logout, openLoginModal } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)
  const isEmployer = (user?.companies.length ?? 0) > 0

  // Fetch unread messages count for logged-in user
  const { data: convData } = useQuery({
    queryKey: ['conversations-unread'],
    queryFn: () => api.conversations(),
    enabled: Boolean(user),
    refetchInterval: 60_000,
  })
  const unreadMessages = convData?.meta?.unread ?? 0

  // Fetch candidate profile for instant avatar sync
  const { data: candidateProfileData } = useQuery({
    queryKey: ['candidate-profile'],
    queryFn: () => api.candidateProfile(),
    enabled: Boolean(user && !isEmployer),
    staleTime: 60_000,
  })

  const rawAvatar =
    candidateProfileData?.data?.avatar_url ||
    candidateProfileData?.data?.user?.avatar_url ||
    user?.avatar_url ||
    candidateProfileData?.data?.user?.avatar_path ||
    user?.avatar_path ||
    null

  const userAvatarUrl = rawAvatar
    ? rawAvatar.startsWith('http')
      ? rawAvatar
      : `${api.baseUrl.replace(/\/api\/v1\/?$/, '')}/storage/${rawAvatar.replace(/^\/?storage\//, '')}`
    : null

  // Close menus when route changes
  useEffect(() => {
    setMobileMenuOpen(false)
    setProfileMenuOpen(false)
  }, [location.pathname])

  // Click outside to close profile dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false)
      }
    }
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [profileMenuOpen])

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (mobileMenuOpen) setMobileMenuOpen(false)
        if (profileMenuOpen) setProfileMenuOpen(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [mobileMenuOpen, profileMenuOpen])

  const handleSignInClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setMobileMenuOpen(false)
    setProfileMenuOpen(false)
    openLoginModal()
  }

  const handleLogout = async () => {
    setMobileMenuOpen(false)
    setProfileMenuOpen(false)
    await logout()
    navigate('/login', { state: { loggedOut: true } })
  }

  if (location.pathname.startsWith('/employer')) return null

  return (
    <>
      <header className="masthead">
        <div className="masthead-inner">
          {/* Left Brand & Primary Links */}
          <div className="masthead-left">
            {/* 1. Logo (redirect to home) */}
            <Link to="/" className="brand" aria-label="FibonacciPi Home">
              <img src="/logo-icon.png" alt="FibonacciPi" style={{ width: '34px', height: '34px', objectFit: 'contain' }} />
              <span className="brand-name">FibonacciPi</span>
            </Link>

            {/* Desktop Primary Navigation Links */}
            <nav className="desktop-nav-primary" aria-label="Primary Navigation">
              {/* 2. Find job */}
              <NavLink to="/jobs" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
                Find Jobs
              </NavLink>

              {/* 3. Fresher Hub */}
              <NavLink to="/freshers" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span>🎓 Fresher Hub</span>
              </NavLink>

              {/* 4. Govt. Jobs (Temporarily hidden until full pipeline moderation is aligned) */}
              {/* <NavLink to="/govt-jobs" className={({ isActive }) => `nav-link govt-jobs-nav-link${isActive ? ' active' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span>🏛️ Govt. Jobs</span>
                <span className="nav-new-pill" style={{ fontSize: '0.68rem', padding: '0.1rem 0.4rem', borderRadius: '999px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', fontWeight: 700, letterSpacing: '0.02em', lineHeight: 1.2 }}>
                  Central & State
                </span>
              </NavLink> */}

              {/* 5. Skill Assessments */}
              <NavLink to="/assessments" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
                Skill Tests
              </NavLink>

              {/* 6. Refer & Earn */}
              <NavLink to="/referrals" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
                Refer & Earn
              </NavLink>

              {/* 7. Safe Hiring */}
              <NavLink to="/safety" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
                🛡️ Safe Hiring
              </NavLink>

              {/* 8. Employers / Job Seekers (based on user role) */}
              {isEmployer ? (
                <NavLink to="/employer/candidates/search" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
                  Job Seekers
                </NavLink>
              ) : (
                <NavLink to="/register?role=employer" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
                  Employers
                </NavLink>
              )}
            </nav>
          </div>

          {/* Right End Navigation Items */}
          <div className="desktop-nav-right">
            {user ? (
              <>
                {/* Notifications */}
                <NotificationBell />

                {/* Message */}
                <NavLink
                  to="/messages"
                  className={({ isActive }) => `nav-icon-btn nav-msg-btn${isActive ? ' active' : ''}`}
                  title="Messages"
                  aria-label={unreadMessages > 0 ? `Messages, ${unreadMessages} unread` : 'Messages'}
                >
                  <svg className="nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  {unreadMessages > 0 && (
                    <span className="nav-badge-count">{unreadMessages > 99 ? '99+' : unreadMessages}</span>
                  )}
                </NavLink>

                {/* Profile dropdown trigger (DDL) */}
                <div className="profile-ddl-container" ref={profileRef}>
                  <button
                    type="button"
                    className={`profile-ddl-trigger ${profileMenuOpen ? 'active' : ''}`}
                    onClick={() => setProfileMenuOpen((prev) => !prev)}
                    aria-expanded={profileMenuOpen}
                    aria-haspopup="true"
                    aria-label="User Profile Menu"
                  >
                    <span className="profile-trigger-avatar">
                      {userAvatarUrl ? (
                        <img
                          src={userAvatarUrl}
                          alt={user.first_name || 'Candidate'}
                          onError={(e) => {
                            (e.currentTarget as HTMLElement).style.display = 'none'
                          }}
                        />
                      ) : (
                        user.first_name ? user.first_name[0]?.toUpperCase() : 'U'
                      )}
                    </span>
                    <span className="profile-trigger-name">
                      {user.first_name || 'Account'}
                    </span>
                    <svg className={`profile-trigger-chevron ${profileMenuOpen ? 'open' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Profile Dropdown Menu (DDL) */}
                  {profileMenuOpen && (
                    <div className="profile-ddl-menu" role="menu">
                      {/* Header Summary */}
                      <div className="profile-ddl-header">
                        <div className="profile-ddl-avatar-large">
                          {userAvatarUrl ? (
                            <img
                              src={userAvatarUrl}
                              alt={user.first_name || 'Candidate'}
                              onError={(e) => {
                                (e.currentTarget as HTMLElement).style.display = 'none'
                              }}
                            />
                          ) : (
                            user.first_name ? user.first_name[0]?.toUpperCase() : 'U'
                          )}
                        </div>
                        <div className="profile-ddl-user-text">
                          <div className="profile-ddl-user-name">
                            {user.first_name} {user.last_name || ''}
                          </div>
                          <div className="profile-ddl-user-email">{user.email}</div>
                          <span className={`role-chip small ${isEmployer ? 'employer' : 'candidate'}`}>
                            {isEmployer ? '💼 Recruiter Account' : '🎯 Job Seeker'}
                          </span>
                        </div>
                      </div>

                      <div className="profile-ddl-divider" />

                      {/* Main Navigation Links in DDL */}
                      <div className="profile-ddl-body">
                        {isEmployer ? (
                          <>
                            <NavLink
                              to="/employer"
                              end
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">💼</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">Recruiter Console</span>
                                <span className="profile-ddl-item-sub">Dashboard & Hiring KPIs</span>
                              </div>
                            </NavLink>
                            <NavLink
                              to="/employer/jobs/new"
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">➕</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">Post a New Job</span>
                                <span className="profile-ddl-item-sub">Create verified vacancy</span>
                              </div>
                            </NavLink>
                            <NavLink
                              to="/employer/pipeline"
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">📋</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">Candidate Pipeline</span>
                                <span className="profile-ddl-item-sub">Manage applicant stages</span>
                              </div>
                            </NavLink>
                            <NavLink
                              to="/employer/candidates/search"
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">🔎</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">Search Talent</span>
                                <span className="profile-ddl-item-sub">Explore verified profiles</span>
                              </div>
                            </NavLink>
                            <NavLink
                              to="/employer/billing"
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">💳</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">Billing & Invoices</span>
                                <span className="profile-ddl-item-sub">Credits and payments</span>
                              </div>
                            </NavLink>
                          </>
                        ) : (
                          <>
                            <NavLink
                              to="/candidate"
                              end
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">📊</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">Candidate Hub</span>
                                <span className="profile-ddl-item-sub">Applications & stats</span>
                              </div>
                            </NavLink>
                            <NavLink
                              to="/candidate/applications"
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">📝</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">My Applications</span>
                                <span className="profile-ddl-item-sub">Track active submissions</span>
                              </div>
                            </NavLink>
                            <NavLink
                              to="/candidate/saved-jobs"
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">⭐</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">Saved Jobs</span>
                                <span className="profile-ddl-item-sub">Bookmarked vacancies</span>
                              </div>
                            </NavLink>
                            <NavLink
                              to="/candidate/recommended"
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">🎯</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">Matched Roles</span>
                                <span className="profile-ddl-item-sub">AI recommended matches</span>
                              </div>
                            </NavLink>
                            <NavLink
                              to="/cvs"
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">📄</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">CV & Resume Builder</span>
                                <span className="profile-ddl-item-sub">Manage & export resumes</span>
                              </div>
                            </NavLink>
                            <NavLink
                              to="/contracts"
                              className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                              onClick={() => setProfileMenuOpen(false)}
                              role="menuitem"
                            >
                              <span className="profile-ddl-item-icon">📑</span>
                              <div className="profile-ddl-item-text">
                                <span className="profile-ddl-item-title">Contracts & Escrow</span>
                                <span className="profile-ddl-item-sub">Freelance milestones</span>
                              </div>
                            </NavLink>
                          </>
                        )}

                        <div className="profile-ddl-divider" />

                        {/* Profile & Settings */}
                        <NavLink
                          to="/candidate/profile"
                          className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                          onClick={() => setProfileMenuOpen(false)}
                          role="menuitem"
                        >
                          <span className="profile-ddl-item-icon">👤</span>
                          <div className="profile-ddl-item-text">
                            <span className="profile-ddl-item-title">My Profile</span>
                            <span className="profile-ddl-item-sub">Edit bio, skills & experience</span>
                          </div>
                        </NavLink>
                        <NavLink
                          to="/candidate/privacy"
                          className={({ isActive }) => `profile-ddl-item${isActive ? ' active' : ''}`}
                          onClick={() => setProfileMenuOpen(false)}
                          role="menuitem"
                        >
                          <span className="profile-ddl-item-icon">⚙️</span>
                          <div className="profile-ddl-item-text">
                            <span className="profile-ddl-item-title">Settings & Privacy</span>
                            <span className="profile-ddl-item-sub">Security & preferences</span>
                          </div>
                        </NavLink>

                        {!isEmployer && (
                          <NavLink
                            to="/register?role=employer"
                            className="profile-ddl-item highlight-employer"
                            onClick={() => setProfileMenuOpen(false)}
                            role="menuitem"
                          >
                            <span className="profile-ddl-item-icon">🏢</span>
                            <div className="profile-ddl-item-text">
                              <span className="profile-ddl-item-title">Employer Console</span>
                              <span className="profile-ddl-item-sub">Hire talent & post jobs</span>
                            </div>
                          </NavLink>
                        )}

                        <div className="profile-ddl-divider" />

                        {/* Sign Out */}
                        <button
                          type="button"
                          className="profile-ddl-signout"
                          onClick={() => void handleLogout()}
                          role="menuitem"
                        >
                          <span className="profile-ddl-item-icon">🚪</span>
                          <span className="profile-ddl-signout-text">Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="nav-auth-group">
                <button
                  type="button"
                  className="nav-btn-signin"
                  onClick={handleSignInClick}
                >
                  Sign In
                </button>
                <NavLink to="/register" className="nav-btn-register">
                  Register Free
                </NavLink>
              </div>
            )}
          </div>

          {/* Mobile Header Quick Actions */}
          <div className="mobile-header-actions">
            {user ? (
              <>
                <NotificationBell />
                <Link to="/candidate/profile" className="mobile-header-avatar" aria-label="My Profile">
                  {userAvatarUrl ? (
                    <img
                      src={userAvatarUrl}
                      alt={user.first_name || 'Candidate'}
                      onError={(e) => {
                        (e.currentTarget as HTMLElement).style.display = 'none'
                      }}
                    />
                  ) : (
                    user.first_name ? user.first_name[0]?.toUpperCase() : 'U'
                  )}
                </Link>
              </>
            ) : (
              <button
                type="button"
                className="mobile-header-signin-btn"
                onClick={handleSignInClick}
                aria-label="Sign In"
              >
                Sign In
              </button>
            )}
            <button
              type="button"
              className={`hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close navigation drawer' : 'Open navigation drawer'}
              aria-expanded={mobileMenuOpen}
            >
              <span className="hamburger-line" />
              <span className="hamburger-line" />
              <span className="hamburger-line" />
            </button>
          </div>
        </div>
      </header>

      {/* Render Mobile Drawer & Backdrop into Document Body via Portal */}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className={`mobile-drawer-portal ${mobileMenuOpen ? 'portal-open' : ''}`} aria-hidden={!mobileMenuOpen}>
            {/* Backdrop Overlay */}
            <div
              className="mobile-drawer-overlay"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Slide-out Mobile App Navigation Drawer */}
            <aside
              className="mobile-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile Navigation Menu"
            >
              {/* Drawer Top App Bar */}
              <div className="mobile-drawer-header">
                <Link to="/" className="brand" onClick={() => setMobileMenuOpen(false)}>
                  <img src="/logo-icon.png" alt="FibonacciPi" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                  <span className="brand-name">FibonacciPi</span>
                  <span className="mobile-app-badge">App</span>
                </Link>
                <button
                  type="button"
                  className="mobile-drawer-close"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close navigation menu"
                >
                  ✕
                </button>
              </div>

              {/* User Hero Profile Card or Guest Welcome Banner */}
              <div className="mobile-drawer-auth">
                {user ? (
                  <div className="mobile-user-card">
                    <div className="mobile-user-info">
                      <span className="mobile-user-avatar">
                        {userAvatarUrl ? (
                          <img
                            src={userAvatarUrl}
                            alt={user.first_name || 'Candidate'}
                            onError={(e) => {
                              (e.currentTarget as HTMLElement).style.display = 'none'
                            }}
                          />
                        ) : (
                          user.first_name ? user.first_name[0]?.toUpperCase() : 'U'
                        )}
                      </span>
                      <div className="mobile-user-text">
                        <div className="mobile-user-name">
                          {user.first_name} {user.last_name || ''}
                        </div>
                        <div className="mobile-user-email">{user.email}</div>
                      </div>
                    </div>
                    <div className="mobile-user-badges">
                      <span className={`role-chip ${isEmployer ? 'employer' : 'candidate'}`}>
                        {isEmployer ? '💼 Recruiter Account' : '🎯 Job Seeker'}
                      </span>
                      <Link
                        to="/candidate/profile"
                        className="mobile-profile-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Edit Profile →
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="mobile-guest-card">
                    <div className="mobile-guest-title">Welcome to FibonacciPi</div>
                    <p className="mobile-guest-subtitle">Discover over 15,000+ verified career & freelance opportunities.</p>
                    <div className="mobile-auth-actions">
                      <button
                        type="button"
                        className="btn btn-primary mobile-btn-signin"
                        onClick={handleSignInClick}
                      >
                        Sign In
                      </button>
                      <Link
                        to="/register"
                        className="btn btn-emerald mobile-btn-register"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Create Free Account
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Navigation Categorized Links */}
              <div className="mobile-drawer-nav">
                {/* Explore & Discover Section */}
                <div className="mobile-nav-section">
                  <div className="mobile-section-title">Explore & Opportunities</div>
                  <NavLink to="/" end className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                    <span className="mobile-nav-icon">🏠</span>
                    <span className="mobile-nav-text">Home Feed</span>
                  </NavLink>
                  <NavLink to="/jobs" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                    <span className="mobile-nav-icon">🔍</span>
                    <span className="mobile-nav-text">Search Jobs</span>
                    <span className="nav-pill-badge">Verified</span>
                  </NavLink>
                  <NavLink to="/freshers" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                    <span className="mobile-nav-icon">🎓</span>
                    <span className="mobile-nav-text">Fresher Hub & Internships</span>
                  </NavLink>
                  {/* <NavLink to="/govt-jobs" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                    <span className="mobile-nav-icon">🏛️</span>
                    <span className="mobile-nav-text">Govt. Jobs (Central & State)</span>
                    <span className="nav-pill-badge" style={{ background: '#f59e0b', color: '#fff' }}>Official</span>
                  </NavLink> */}
                  <NavLink to="/assessments" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                    <span className="mobile-nav-icon">📝</span>
                    <span className="mobile-nav-text">Skill Tests & Badges</span>
                  </NavLink>
                  <NavLink to="/referrals" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                    <span className="mobile-nav-icon">🎁</span>
                    <span className="mobile-nav-text">Refer & Earn (₹500)</span>
                  </NavLink>
                  <NavLink to="/safety" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                    <span className="mobile-nav-icon">🛡️</span>
                    <span className="mobile-nav-text">Safe Hiring Guarantee</span>
                  </NavLink>
                  <NavLink to="/companies" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                    <span className="mobile-nav-icon">🏢</span>
                    <span className="mobile-nav-text">Top Companies</span>
                  </NavLink>
                  <NavLink to="/salaries" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                    <span className="mobile-nav-icon">💰</span>
                    <span className="mobile-nav-text">Salary Benchmarks</span>
                  </NavLink>
                  <NavLink to="/projects" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                    <span className="mobile-nav-icon">⚡</span>
                    <span className="mobile-nav-text">Freelance Projects</span>
                  </NavLink>
                </div>

                {/* Candidate Portal Section */}
                {user && (
                  <div className="mobile-nav-section">
                    <div className="mobile-section-title">Candidate Workspace</div>
                    <NavLink to="/candidate" end className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                      <span className="mobile-nav-icon">📊</span>
                      <span className="mobile-nav-text">Dashboard Overview</span>
                    </NavLink>
                    <NavLink to="/candidate/applications" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                      <span className="mobile-nav-icon">📝</span>
                      <span className="mobile-nav-text">My Applications</span>
                    </NavLink>
                    <NavLink to="/candidate/saved-jobs" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                      <span className="mobile-nav-icon">⭐</span>
                      <span className="mobile-nav-text">Saved Jobs</span>
                    </NavLink>
                    <NavLink to="/candidate/recommended" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                      <span className="mobile-nav-icon">🎯</span>
                      <span className="mobile-nav-text">Matched Roles</span>
                    </NavLink>
                    <NavLink to="/cvs" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                      <span className="mobile-nav-icon">📄</span>
                      <span className="mobile-nav-text">CV & Resume Builder</span>
                    </NavLink>
                    <NavLink to="/messages" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                      <span className="mobile-nav-icon">💬</span>
                      <span className="mobile-nav-text">Messages</span>
                    </NavLink>
                    <NavLink to="/contracts" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                      <span className="mobile-nav-icon">📑</span>
                      <span className="mobile-nav-text">Contracts & Invoices</span>
                    </NavLink>
                  </div>
                )}

                {/* Employer Portal Section */}
                <div className="mobile-nav-section">
                  <div className="mobile-section-title">Recruiter & Employer</div>
                  {isEmployer ? (
                    <>
                      <NavLink to="/employer" end className={({ isActive }) => `mobile-nav-link highlight${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        <span className="mobile-nav-icon">💼</span>
                        <span className="mobile-nav-text">Recruiter Console</span>
                      </NavLink>
                      <NavLink to="/employer/jobs/new" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        <span className="mobile-nav-icon">➕</span>
                        <span className="mobile-nav-text">Post a New Job</span>
                      </NavLink>
                      <NavLink to="/employer/pipeline" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        <span className="mobile-nav-icon">📋</span>
                        <span className="mobile-nav-text">Candidate Pipeline</span>
                      </NavLink>
                      <NavLink to="/employer/candidates/search" className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                        <span className="mobile-nav-icon">🔎</span>
                        <span className="mobile-nav-text">Search Talent</span>
                      </NavLink>
                    </>
                  ) : (
                    <NavLink to="/register?role=employer" className="mobile-nav-link highlight" onClick={() => setMobileMenuOpen(false)}>
                      <span className="mobile-nav-icon">🏢</span>
                      <span className="mobile-nav-text">Post Jobs & Hire Talent</span>
                    </NavLink>
                  )}
                </div>

                {/* Footer Controls */}
                {user && (
                  <div className="mobile-drawer-footer">
                    <button
                      type="button"
                      className="btn btn-outline mobile-btn-signout"
                      onClick={() => void handleLogout()}
                    >
                      <span className="btn-icon">🚪</span> Sign Out of Account
                    </button>
                  </div>
                )}

                <div className="mobile-app-version-info">
                  <span>FibonacciPi Mobile · v2.4</span>
                </div>
              </div>
            </aside>
          </div>,
          document.body
        )}
    </>
  )
}

